<template>
</template>

<script>
    import {EventBus} from './mixins/EventBus';
    import {ViewDates} from './mixins/ViewDatesMixin';
    import {isBlockingEvent, isReservationEvent} from './helpers/blockingEvents';
    import {yToMinutes} from './helpers/dragGeometry';
    import {selectionRange, clampRangeToGap} from './helpers/slotSelection';
    import SlotMenu from './SlotMenu.vue';

    // Mouse: moving beyond this after pressing on the grid turns the press into a
    // range selection instead of a click.
    const SELECT_THRESHOLD_PX = 5;

    export default {
        mixins: [EventBus, ViewDates],

        components: { 'slot-menu': SlotMenu },

        props: {
            action: {
                default: 'show'
            },
            canReserve: {
                // Whether a click on the empty grid may also create a reservation
                // (instructor calendars with a reservation endpoint). Off, the click
                // goes straight to the event modal as it always did.
                default: false
            },
            canCreate: {
                // Whether the empty grid can take a new event at all. The calendar
                // list renders the grid before an instructor is picked, and then
                // there is nowhere to store what the modal would collect: Save
                // silently did nothing. With this off the grid does not offer
                // (hover, sweep, modal) what it cannot deliver.
                default: true
            },
            events: {
                default: () => []
            },
            eventTemplates: {
                default: () => []
            },
            date: {
                default: () => new Date()
            },
            highlighted: {
                default: null
            },
            defaultCalendarStartHour: {
                default: 9
            },
            defaultCalendarEndHour: {
                default: 19
            },
            blockSchedulingAfterHour: {
                default: 24 // 24 - will not block scheduling
            },
            rowHeight: {
                default: 44
            },
            snapMinutes: {
                default: 15
            },
            slotSnapMinutes: {
                // Step the empty grid is picked in: a click at 9:40 starts the new
                // block at 9:30, and a sweep grows in cells of this size. Used to be
                // whole hours, which made the half-hour slots of a work schedule
                // unreachable without a trip through the modal's time inputs.
                default: 30
            },
            preventOverlap: {
                // Slide a new event out of the way of existing ones on the same day
                // instead of letting it be created on top of them (work schedule).
                default: false
            },
            defaultDurationInMinutes: {
                default: 120
            },
            dragMode: {
                default: 'manager' // 'manager' | 'student' | 'off'
            },
            restrictPracticeToFreeSlots: {
                default: false // managers: restrict practice-lesson drags to free slots only
            },
            hidePastEvents: {
                default: false
            },
            hideVacationEvents: {
                default: false
            },
            texts: {
                default: () => {
                }
            },
        },

        data() {
            return {
                calendarStartHour: this.defaultCalendarStartHour,
                calendarEndHour: this.defaultCalendarEndHour,
                eventsByDateMap: {},
                eventTemplatesByDateMap: {},
                eventsByDates: {},
                eventTemplatesByDates: {},
                // How far an event pulls its right edge in for each block drawn behind
                // it, and how many such steps it will take before it stops shrinking.
                overlapStepWidth: 14,
                maxOverlapSteps: 3,
                // Preview of the event a click on the hovered empty cell would create:
                // { dateKey, startMin, endMin } or null. Doubles as the live preview
                // of a range being swept, and of the range a menu is open for.
                hoverSlot: null,
                // Press on the empty grid that may turn into a range selection:
                // { date, anchorMin, gridTop, pointerId, startX, startY, started, range }.
                gridSelect: null,
                // "Eveniment / Rezervare" popover for a picked slot:
                // { date, start, end, x, y } or null.
                slotMenu: null,
            }
        },

        mounted() {
            // console.log('Timetable View Component Mounted.', Date.now());
            this.updateEventMapsByDates();
        },

        beforeDestroy() {
            this.teardownGridSelect();
        },

        methods: {
            addNewEvent(date, hour, domEvent) {
                // The click that ends a drag lands on the same cell it started on;
                // the drag already handled it.
                if (this.gridSelectJustEnded) return;

                this.clearHoverSlot();

                if (!this.canCreate) {
                    this.eventBus.$emit('create-event-unavailable');
                    return;
                }

                const slot = this.resolveNewEventSlot(date, this.pointerMinutes(domEvent, hour));
                if (!slot) {
                    this.notifyNoRoom();
                    return;
                }

                this.offerNewSlot(date, slot.start, slot.end, domEvent);
            },

            notifyNoRoom() {
                this.$notify({
                    group: 'app',
                    type: 'error',
                    text: (this.texts && this.texts.no_room_for_event)
                        || 'Nu este suficient spațiu liber aici pentru un loc nou. Alegeți altă oră.',
                });
            },

            /**
             * A slot was picked on the empty grid (clicked or swept). Where the
             * calendar can also take reservations, ask what to put there; anywhere
             * else there is only one thing it can be, so open the event modal
             * directly. `end` is null when the click left the duration to the modal.
             */
            offerNewSlot(date, start, end, domEvent, eventTemplate = null) {
                if (!this.canReserve) {
                    this.emitAddNewEvent(date, start, end);
                    return;
                }

                const previewEnd = end || moment(start).add(this.defaultDurationInMinutes, 'minutes');
                const dayStart = moment(date).startOf('day');
                this.hoverSlot = {
                    dateKey: dayStart.format('YYYY-MM-DD'),
                    startMin: start.diff(dayStart, 'minutes'),
                    endMin: previewEnd.diff(dayStart, 'minutes'),
                    picked: true,
                };

                this.slotMenu = {
                    date,
                    start,
                    end,
                    // Set when a single free slot was clicked: "Eveniment" then keeps
                    // the slot's own title and type, as the plain click always did.
                    eventTemplate,
                    x: domEvent ? domEvent.clientX : 0,
                    y: domEvent ? domEvent.clientY : 0,
                };
            },

            slotMenuLabel() {
                const menu = this.slotMenu;
                const end = menu.end || moment(menu.start).add(this.defaultDurationInMinutes, 'minutes');
                return moment(menu.date).format('ddd D MMM') + ' · '
                    + menu.start.format('HH:mm') + ' – ' + end.format('HH:mm');
            },

            pickSlotAction(what) {
                const menu = this.slotMenu;
                this.closeSlotMenu();
                if (!menu) return;

                if (what === 'reservation') {
                    const end = menu.end || moment(menu.start).add(this.defaultDurationInMinutes, 'minutes');
                    this.eventBus.$emit('add-new-reservation', menu.date.toDate(), menu.start, end);
                    return;
                }

                if (menu.eventTemplate) {
                    this.eventBus.$emit('click-event-template', menu.eventTemplate, menu.date);
                    return;
                }

                this.emitAddNewEvent(menu.date, menu.start, menu.end);
            },

            closeSlotMenu() {
                this.slotMenu = null;
                this.clearHoverSlot();
            },

            emitAddNewEvent(date, start, end) {
                if (end) {
                    this.eventBus.$emit('add-new-event', date.toDate(), start, end);
                    return;
                }

                this.eventBus.$emit('add-new-event', date.toDate(), start);
            },

            // ---- Click-and-drag over the empty grid ---------------------------------
            //
            // Sweeping over several free cells picks all of them at once, so a block
            // of a few hours takes one gesture instead of a click and a trip through
            // the modal's time inputs. Mouse and pen only: on touch a swipe scrolls.

            onGridPointerDown(date, domEvent) {
                this.startGridSelect(date, domEvent, null);
            },

            // Pressing on a free slot starts the same sweep, anchored at the slot's
            // own start so the range never begins mid-slot.
            onTemplatePointerDown(eventTemplate, date, domEvent) {
                if (eventTemplate._start_hour >= this.blockSchedulingAfterHour) return;
                this.startGridSelect(date, domEvent, eventTemplate._start_on_minute);
            },

            startGridSelect(date, domEvent, anchorMin) {
                if (domEvent.pointerType === 'touch') return;
                if (domEvent.button != null && domEvent.button !== 0) return;
                if (this.action !== 'edit' || !this.canCreate || this.vacationSelectMode || this.drag) return;

                // Both the grid and the blocks are laid out from the column content's
                // top, at calendarStartHour.
                const column = domEvent.currentTarget.closest('.timetable-column');
                const content = column && column.querySelector('.timetable-column-content');
                if (!content) return;
                const gridTop = content.getBoundingClientRect().top;

                this.closeSlotMenu();
                this.gridSelect = {
                    date,
                    gridTop,
                    anchorMin: anchorMin != null
                        ? anchorMin
                        : yToMinutes(domEvent.clientY - gridTop, this.rowHeight, this.calendarStartHour),
                    pointerId: domEvent.pointerId,
                    startX: domEvent.clientX,
                    startY: domEvent.clientY,
                    started: false,
                    range: null,
                };

                window.addEventListener('pointermove', this.onGridPointerMove);
                window.addEventListener('pointerup', this.onGridPointerUp);
                window.addEventListener('pointercancel', this.onGridPointerCancel);
            },

            onGridPointerMove(domEvent) {
                const sel = this.gridSelect;
                if (!sel || domEvent.pointerId !== sel.pointerId) return;

                if (!sel.started) {
                    if (Math.hypot(domEvent.clientX - sel.startX, domEvent.clientY - sel.startY) < SELECT_THRESHOLD_PX) return;
                    sel.started = true;
                    document.body.classList.add('timetable-selecting');
                    // Whatever the first few pixels of the press highlighted.
                    if (window.getSelection) window.getSelection().removeAllRanges();
                }

                const pointerMin = yToMinutes(domEvent.clientY - sel.gridTop, this.rowHeight, this.calendarStartHour);
                let range = selectionRange(
                    sel.anchorMin, pointerMin, this.slotSnapMinutes, this.calendarStartHour, this.calendarEndHour
                );

                if (this.preventOverlap) {
                    range = clampRangeToGap(range, this.busyRangesForDate(sel.date), sel.anchorMin);
                }

                if (range) {
                    range = this.expandToFreeSlots(range, sel.date);
                }

                sel.range = range;
                this.hoverSlot = range
                    ? { dateKey: moment(sel.date).format('YYYY-MM-DD'), picked: true, ...range }
                    : null;
                this.updateSelectGhost(domEvent.clientX, domEvent.clientY, range);
            },

            onGridPointerUp(domEvent) {
                const sel = this.gridSelect;
                if (!sel || domEvent.pointerId !== sel.pointerId) return;
                this.teardownGridSelect();

                // A plain click: the cell's own @click handler takes it from here.
                if (!sel.started) return;

                // The click event that follows this pointerup must not also open
                // the plain single-cell flow.
                this.gridSelectJustEnded = true;
                setTimeout(() => { this.gridSelectJustEnded = false; }, 0);

                if (!sel.range) {
                    this.clearHoverSlot();
                    this.notifyNoRoom();
                    return;
                }

                const dayStart = moment(sel.date).startOf('day');
                this.offerNewSlot(
                    sel.date,
                    dayStart.clone().add(sel.range.startMin, 'minutes'),
                    dayStart.clone().add(sel.range.endMin, 'minutes'),
                    domEvent
                );
            },

            onGridPointerCancel(domEvent) {
                const sel = this.gridSelect;
                if (!sel || domEvent.pointerId !== sel.pointerId) return;
                this.teardownGridSelect();
                this.clearHoverSlot();
            },

            teardownGridSelect() {
                window.removeEventListener('pointermove', this.onGridPointerMove);
                window.removeEventListener('pointerup', this.onGridPointerUp);
                window.removeEventListener('pointercancel', this.onGridPointerCancel);
                document.body.classList.remove('timetable-selecting');
                if (this.selectGhost) {
                    this.selectGhost.remove();
                    this.selectGhost = null;
                }
                this.gridSelect = null;
            },

            // Same floating time label the reschedule drag shows, so the two
            // gestures read alike.
            updateSelectGhost(clientX, clientY, range) {
                if (!this.selectGhost) {
                    this.selectGhost = document.createElement('div');
                    this.selectGhost.className = 'timetable-drag-ghost';
                    document.body.appendChild(this.selectGhost);
                }
                const fmt = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(Math.round(m % 60)).padStart(2, '0')}`;
                this.selectGhost.textContent = range
                    ? `${fmt(range.startMin)} – ${fmt(range.endMin)}`
                    : ((this.texts && this.texts.no_room_for_event_short) || '—');
                this.selectGhost.classList.toggle('invalid', !range);
                this.selectGhost.style.left = clientX + 12 + 'px';
                this.selectGhost.style.top = clientY - 28 + 'px';
            },

            // A sweep that touches a free slot takes the whole slot: the slots are
            // the units the instructor thinks in, so a block should cover them
            // exactly, not stop a half-hour into one.
            expandToFreeSlots(range, date) {
                let { startMin, endMin } = range;

                (this.eventTemplatesByDates[date] || []).forEach((template) => {
                    if (!this.showEventTemplate(template, date)) return;
                    if (template._end_on_minute <= startMin || template._start_on_minute >= endMin) return;
                    startMin = Math.min(startMin, template._start_on_minute);
                    endMin = Math.max(endMin, template._end_on_minute);
                });

                return { startMin, endMin };
            },

            busyRangesForDate(date) {
                const toMinutes = (time) => time.hour() * 60 + time.minute();
                return (this.eventsByDates[date] || []).map((event) => ({
                    from: toMinutes(event._start_time),
                    to: toMinutes(event._end_time),
                }));
            },

            /**
             * Minute of the day under the pointer on a grid cell, floored to the
             * `slotSnapMinutes` step. Falls back to the cell's hour when there is
             * no pointer (keyboard, tests).
             */
            pointerMinutes(domEvent, hour) {
                if (!domEvent || !domEvent.currentTarget || !domEvent.currentTarget.parentNode) {
                    return hour * 60;
                }

                const gridTop = domEvent.currentTarget.parentNode.getBoundingClientRect().top;
                const minutes = yToMinutes(domEvent.clientY - gridTop, this.rowHeight, this.calendarStartHour);

                return Math.floor(minutes / this.slotSnapMinutes) * this.slotSnapMinutes;
            },

            /**
             * Where a click at `startMin` (minute of the day) on `date` would put a
             * new event: `{ start, end }` moments (`end` only when the slot was
             * fitted into a free gap), or null when there is no room. Shared by the
             * click and by the hover placeholder, so what is previewed is what gets
             * created.
             */
            resolveNewEventSlot(date, startMin) {
                let start_time = moment(date).startOf('day').add(startMin, 'minutes');
                let event_start_time = moment(date);
                let event_end_time = moment(date);

                if (this.eventsByDates[date]) {
                    this.eventsByDates[date].forEach((event) => {
                        event_start_time
                            .hour(event._start_time.hour())
                            .minute(event._start_time.minute());

                        event_end_time
                            .hour(event._end_time.hour())
                            .minute(event._end_time.minute());

                        if (start_time >= event_start_time && start_time < event_end_time) {
                            start_time = event._end_time;
                        }
                    });
                }

                if (this.preventOverlap) {
                    return this.fitIntoFreeGap(date, start_time);
                }

                return { start: start_time, end: null };
            },

            /**
             * Hover placeholder: while the pointer rests on an empty cell, draw the
             * block a click there would create (same look as the drag placeholder).
             */
            hoverNewEventSlot(date, hour, domEvent) {
                // A sweep in progress or an open menu owns the placeholder.
                if (this.gridSelect || this.slotMenu) return;

                if (this.action !== 'edit' || !this.canCreate || this.vacationSelectMode || this.drag) {
                    this.clearHoverSlot();
                    return;
                }

                const slot = this.resolveNewEventSlot(date, this.pointerMinutes(domEvent, hour));
                if (!slot) {
                    this.clearHoverSlot();
                    return;
                }

                const end = slot.end || moment(slot.start).add(this.defaultDurationInMinutes, 'minutes');
                const dayStart = moment(date).startOf('day');
                const next = {
                    dateKey: dayStart.format('YYYY-MM-DD'),
                    startMin: slot.start.diff(dayStart, 'minutes'),
                    endMin: end.diff(dayStart, 'minutes'),
                };

                // Fed by mousemove: only touch state when the preview actually moves.
                const cur = this.hoverSlot;
                if (cur && cur.dateKey === next.dateKey && cur.startMin === next.startMin && cur.endMin === next.endMin) {
                    return;
                }

                this.hoverSlot = next;
            },

            clearHoverSlot() {
                this.hoverSlot = null;
            },

            // The grid's mouseleave: leaving it mid-sweep or with the menu open
            // must keep the preview of what is being picked.
            leaveGrid() {
                if (this.gridSelect || this.slotMenu) return;
                this.clearHoverSlot();
            },

            isHoverSlotOn(date) {
                return !!this.hoverSlot && this.hoverSlot.dateKey === moment(date).format('YYYY-MM-DD');
            },

            hoverSlotStyle() {
                const slot = this.hoverSlot;
                return {
                    top: this.rowHeight * (slot.startMin / 60 - this.calendarStartHour) + 'px',
                    height: this.rowHeight * ((slot.endMin - slot.startMin) / 60) + 'px',
                };
            },

            /**
             * Place a default-length event at `start_time` without crossing any
             * existing event on `date`. When the clicked cell sits just above an
             * event, the new one is pulled up so that it ends where that event
             * starts (#106). Returns null when the gap is too small.
             */
            fitIntoFreeGap(date, start_time) {
                const duration = this.defaultDurationInMinutes;
                const dayStart = moment(date).startOf('day');
                const toMinutes = (time) => time.hour() * 60 + time.minute();

                const busy = (this.eventsByDates[date] || [])
                    .map((event) => ({
                        from: toMinutes(event._start_time),
                        to: toMinutes(event._end_time),
                    }))
                    .sort((a, b) => a.from - b.from);

                let start = toMinutes(start_time);
                let gapStart = 0;
                let gapEnd = 24 * 60;

                busy.forEach((slot) => {
                    if (slot.to <= start && slot.to > gapStart) gapStart = slot.to;
                    if (slot.from > start && slot.from < gapEnd) gapEnd = slot.from;
                });

                if (gapEnd - gapStart < duration) return null;

                if (start + duration > gapEnd) start = gapEnd - duration;
                if (start < gapStart) start = gapStart;

                return {
                    start: dayStart.clone().add(start, 'minutes'),
                    end: dayStart.clone().add(start + duration, 'minutes'),
                };
            },

            clickEvent(event, date) {
                this.eventBus.$emit('click-event', event, date);
            },

            clickEventTemplate(eventTemplate, date, domEvent) {
                // The click that ends a sweep lands on the block it started on.
                if (this.gridSelectJustEnded) return;

                if (eventTemplate._start_hour >= this.blockSchedulingAfterHour) {
                    return;
                }

                // With nobody to schedule, a free slot is just a time range the
                // instructor may want blocked instead of booked -- same question as
                // on the empty grid.
                if (this.action === 'edit' && this.canReserve) {
                    const dayStart = moment(date).startOf('day');
                    this.offerNewSlot(
                        date,
                        dayStart.clone().add(eventTemplate._start_on_minute, 'minutes'),
                        dayStart.clone().add(eventTemplate._end_on_minute, 'minutes'),
                        domEvent,
                        eventTemplate
                    );
                    return;
                }

                this.eventBus.$emit('click-event-template', eventTemplate, date);
            },

            clickFreeEventTemplate(eventTemplate, date) {
                if (eventTemplate._created_event) {
                    this.eventBus.$emit('click-event', eventTemplate._created_event, date);
                } else {
                    this.eventBus.$emit('click-free-event-template', eventTemplate, date);
                }
            },

            getEventsByDate(date) {
                return this.events.filter((event) => {
                    if (event.is_recurring) {
                        return event._recurrence && event._recurrence.matches(date);
                    } else {
                        return event._start_time.isSame(date, 'day');
                    }
                });
            },

            getEventTemplatesByDate(date) {
                if (!moment(date).isValid()) return [];

                return this.eventTemplates.filter((event) => {
                    if (event.is_recurring) {
                        return event._recurrence && event._recurrence.matches(date);
                    } else {
                        return event._start_time.isSame(date, 'day');
                    }
                });
            },

            isEventHighlighted(event) {
                if (!this.highlighted) return false;

                if (typeof this.highlighted === 'function') {
                    return this.highlighted(event);
                } else if (typeof this.highlighted === 'object') {
                    if (this.highlighted.contains) {
                        return event[this.highlighted.contains.key].indexOf(this.highlighted.contains.value) + 1;
                    }
                }
            },

            openDayView(date) {
                this.eventBus.$emit('date-changed', date.toDate());
                this.eventBus.$emit('view-changed', 'day');
            },

            updateEventMapsByDates() {
                this.eventsByDates = {};
                this.eventTemplatesByDates = {};

                this.dates.forEach((date) => {
                    let dateKey = date.unix();

                    this.eventsByDateMap[dateKey] = this.eventsByDateMap[dateKey] || [];
                    this.eventsByDates[date] = this.eventsByDateMap[dateKey];

                    this.eventTemplatesByDateMap[dateKey] = this.eventTemplatesByDateMap[dateKey] || [];
                    this.eventTemplatesByDates[date] = this.eventTemplatesByDateMap[dateKey];
                });

                this.$nextTick(() => {
                    this.calendarStartHour = this.defaultCalendarStartHour;
                    this.calendarEndHour = this.defaultCalendarEndHour;

                    this.dates.forEach((date) => {
                        let dateKey = date.unix();

                        this.eventsByDateMap[dateKey] = this.getEventsByDate(date);
                        this.eventsByDates[date] = this.eventsByDateMap[dateKey];

                        this.eventTemplatesByDateMap[dateKey] = this.getEventTemplatesByDate(date);
                        this.eventTemplatesByDates[date] = this.eventTemplatesByDateMap[dateKey];

                        // Fix calendar start and end hours
                        this.eventsByDates[date].forEach((event) => {
                            // Full-day holidays must not widen the visible range with
                            // empty non-working hours (00:00–08:00, 21:00–24:00); they
                            // render as a backdrop spanning whatever range the real
                            // events/templates define. Partial holidays keep their real
                            // hours, so they count toward the range like any timed event.
                            if (isBlockingEvent(event) && event.is_full_day_event) return;
                            if (this.calendarStartHour > event._start_hour)
                                this.calendarStartHour = event._start_hour;
                            if (this.calendarEndHour < event._end_hour)
                                this.calendarEndHour = event._end_hour;
                        });
                        this.eventTemplatesByDates[date].forEach((eventTemplate) => {
                            if (this.calendarStartHour > eventTemplate._start_hour)
                                this.calendarStartHour = eventTemplate._start_hour;
                            if (this.calendarEndHour < eventTemplate._end_hour)
                                this.calendarEndHour = eventTemplate._end_hour;
                        });
                    });

                    this.$forceUpdate();
                });
            },

            freeSlotsForDate(date, excludeEventId) {
                const templates = this.eventTemplatesByDates[date] || [];
                const events = (this.eventsByDates[date] || []).filter(e => e.id !== excludeEventId);

                return templates
                    .filter(t => {
                        if (t._start_hour >= this.blockSchedulingAfterHour) return false;
                        return !events.some(e =>
                            t._start_on_minute < e._end_on_minute && e._start_on_minute < t._end_on_minute);
                    })
                    .map(t => ({ startMin: t._start_on_minute, endMin: t._end_on_minute }));
            },

            // Holidays stay in `events` even when they are not drawn: showEventTemplate()
            // below reads them to hide the instructor's free slots on days off, so
            // filtering them out of the data would put those slots back on the calendar.
            showEvent(event) {
                if (isBlockingEvent(event)) return !this.hideVacationEvents;

                return !event.disabled;
            },

            // Label printed on a calendar block. A block with no title of its own
            // reads "Liber" -- except an exam slot, which is anything but free: the
            // school opens it so students can book, so it reads "Examen".
            eventLabel(event) {
                if (event.display_title) return event.display_title;
                if (event.type === 'internal-exam') return this.texts.exam || this.texts.no_title;

                return this.texts.no_title;
            },

            // Below this height the two stacked lines (title + hours) no longer fit,
            // so the block switches to a single clipped line. 34px is the two-line
            // stack (14 + 4 + 13 + 1 + a little breathing room) at the default row height.
            isCompactBlock(durationInHours) {
                return this.rowHeight * durationInHours < 36;
            },

            // How many title lines fit above the hours line in a block this tall: the
            // 14px name lines share the block with the 13px hours line and its 4 + 1px
            // margins. A long exam title is clipped there instead of spilling out.
            nameLineClamp(durationInHours) {
                return Math.max(1, Math.floor((this.rowHeight * durationInHours - 18) / 14));
            },

            // Holiday or any other reservation: same backdrop mechanics for both.
            isBlockingEvent(event) {
                return isBlockingEvent(event);
            },

            isReservationEvent(event) {
                return isReservationEvent(event);
            },

            manageVacation(event) {
                this.eventBus.$emit('manage-vacation', event);
            },

            // Every holiday drawn on a day -- what the day header's manage button offers.
            // A day can hold several (a morning and an afternoon absence, say), so the
            // header has to name them rather than silently act on whichever came first.
            // A holiday spanning several days is one event per day, so the one landing
            // on this date identifies the whole group downstream.
            vacationsForDate(date) {
                return (this.eventsByDates[date] || [])
                    .filter((event) => isBlockingEvent(event) && this.showEvent(event));
            },

            // An event painted over another hides it completely -- most visibly a lesson
            // covering the holiday backdrop, taking the manage button in its corner with
            // it. Every event that overlaps a block drawn behind it is nudged right by a
            // slice instead, leaving a strip of that block visible and clickable.
            overlapInset(event, date) {
                // The backdrop is the bottom layer; nothing is behind it to reveal.
                if (isBlockingEvent(event)) return 0;

                const siblings = (this.eventsByDates[date] || []).filter((e) => this.showEvent(e));
                const index = siblings.indexOf(event);
                let behind = 0;

                siblings.forEach((other, otherIndex) => {
                    if (other === event) return;
                    if (other._end_on_minute <= event._start_on_minute) return;
                    if (other._start_on_minute >= event._end_on_minute) return;

                    // Whatever starts earlier reads as being behind; equal starts fall
                    // back to render order, which is what actually decides the stacking.
                    const isBehind = other._start_on_minute === event._start_on_minute
                        ? otherIndex < index
                        : other._start_on_minute < event._start_on_minute;

                    if (isBehind) behind++;
                });

                return Math.min(behind, this.maxOverlapSteps) * this.overlapStepWidth;
            },

            eventBlockStyle(event, date) {
                let style;

                if (isBlockingEvent(event) && event.is_full_day_event) {
                    // Full-day holidays fill the whole visible range as a backdrop rather
                    // than being positioned by their 00:00–23:59 times (which would push
                    // the calendar to show empty non-working hours). Partial holidays keep
                    // their real hours and are laid out like any other timed event.
                    style = {
                        top: '0px',
                        height: this.rowHeight * (this.calendarEndHour - this.calendarStartHour + 1) + 'px',
                    };
                } else {
                    style = this.lo.merge(
                        {
                            top: this.rowHeight * (event._offset - this.calendarStartHour) + 'px',
                            height: this.rowHeight * event._duration_in_hours + 'px',
                        },
                        event.custom_css
                    );
                }

                const inset = this.overlapInset(event, date);
                if (inset) {
                    style.left = inset + 'px';
                    style.width = 'calc(100% - ' + inset + 'px)';
                }

                return style;
            },

            showEventTemplate(eventTemplate, date) {
                if (eventTemplate._start_hour >= this.blockSchedulingAfterHour) return false;

                if (!this.eventsByDates[date]) return true;

                let count = this.eventsByDates[date].length;
                for (let i=0; i<count; i++) {
                    if (eventTemplate._end_on_minute <= this.eventsByDates[date][i]._start_on_minute) continue;
                    if (eventTemplate._start_on_minute >= this.eventsByDates[date][i]._end_on_minute) continue;
                    return false;
                }

                return true;
            },
        },
        computed: {
            hours() {
                let hours = [];
                let startHour = this.calendarStartHour;
                let endHour = this.calendarEndHour;

                for (let hour = startHour; hour <= endHour; hour++) {
                    hours.push(hour);
                }
                return hours;
            },

            dates() {
                const today = moment();
                let dates = [];
                let date = moment(this.viewStartDate);

                for (let i = 0; i < this.viewNumberOfDays; i++) {
                    date.isToday = today.isSame(date, 'day');

                    dates.push(date);
                    date = moment(date).add(1, 'days');
                }

                return dates;
            },

            orderedEvents() {
                return this.events.sort((e1, e2) => {
                    if (e1._start_time < e2._start_time) return -1;
                    if (e1._start_time > e2._start_time) return 1;
                    return 0;
                });
            },

            now() {
                return moment();
            }
        },

        filters: {
            pad(num) {
                if (num < 10) return '0' + num;
                return num;
            }
        },

        watch: {
            dates() {
                this.updateEventMapsByDates();
            },

            events() {
                this.updateEventMapsByDates();
            },
        }
    }
</script>
<style>
</style>
